import { useEffect, type ComponentType } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { SetupStatusView, SetupWizardStepKey } from '@pqrs/contracts';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Progress } from '@xiltrion/ui/progress';
import { Spinner } from '@xiltrion/ui/spinner';
import { Stepper, StepperIndicator, StepperItem, StepperNav, StepperSeparator, StepperTitle, StepperTrigger } from '@xiltrion/ui/stepper';
import { ErrorState, LoadingState, PageHeader } from '@/components/ui';
import { useSession } from '@/app/session';
import { AdminHost, confirm, notify, notifyError } from '@/features/config/shared';
import { CONFIG_SECTIONS } from '@/features/config/ConfigLayout';
import { EntityPage } from '@/features/config/EntityPage';
import { OrganizationPage } from '@/features/config/OrganizationPage';
import { UsersPage } from '@/features/config/UsersPage';
import { TriagePeopleCard } from '@/features/config/SettingsPages';
import { blockedBy, setupIssues, setupSteps, useGoLive, useRecheckSetup, useSetupStatus, type SetupStepView } from './api';
import { SetupChecklist } from './SetupChecklist';
import { SuitePendingPage } from './SuitePendingPage';

/** Lo que resuelve cada paso, y nada más: la pantalla de Configuración o solo la parte que le toca (áreas sin personas, triaje). */
const SCREENS: Record<SetupWizardStepKey, ComponentType> = {
  entidad: EntityPage,
  areas: () => <OrganizationPage onlyAreas />,
  usuarios: () => <UsersPage inSetup />,
  funciones: () => <OrganizationPage />,
  triaje: TriagePeopleCard,
};
/** El color del indicador sale del estado real del paso, no de si va antes o después del actual. */
const INDICATOR = {
  done: 'data-[state=completed]:bg-success data-[state=completed]:text-success-foreground data-[state=inactive]:bg-success data-[state=inactive]:text-success-foreground',
  missing: 'data-[state=completed]:bg-destructive/10 data-[state=completed]:text-destructive data-[state=inactive]:bg-destructive/10 data-[state=inactive]:text-destructive',
  waiting: 'data-[state=completed]:bg-accent data-[state=completed]:text-accent-foreground',
};
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Puesta en marcha: los pasos en orden, cada uno con sus requisitos y la pantalla de Configuración que los resuelve;
 * un paso se abre cuando el anterior cumple lo obligatorio, y el último termina en el paso a operación. Con la
 * entidad ya en operación es el estado de la configuración (mismos requisitos, todos los pasos abiertos, sin el botón).
 */
export function SetupPage() {
  const q = useSetupStatus();
  const goLive = useGoLive();
  const recheck = useRecheckSetup();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  // El paso queda en la dirección: al cumplir sus requisitos no salta solo al siguiente mientras se trabaja en él.
  useEffect(() => {
    if (!q.data || params.get('paso')) return;
    const steps = setupSteps(q.data.checks);
    setParams({ paso: (steps.find((s) => !s.done) ?? steps[steps.length - 1]!).key }, { replace: true });
  }, [q.data, params, setParams]);

  if (q.isLoading) return <LoadingState rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  const st = q.data;
  const setup = st.status === 'SETUP';
  // Sin el correo y el almacenamiento que asigna la suite, la puesta en marcha no empieza.
  if (setup && st.suite && !st.suite.ready) return <SuitePendingPage suite={st.suite} />;
  const steps = setupSteps(st.checks);
  const reachable = (n: number) => !setup || !blockedBy(steps[n - 1]!, steps);
  const asked = steps.findIndex((s) => s.key === params.get('paso')) + 1;
  const firstPending = steps.findIndex((s) => !s.done) + 1;
  const wanted = asked || firstPending || steps.length;
  const current = reachable(wanted) ? wanted : firstPending;
  const go = (n: number) => setParams({ paso: steps[n - 1]!.key }, { replace: true });
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  const pasarAOperacion = async () => {
    // Lo que la entidad ya traía y falla (correo, motor…) no detiene: se dice, y se sigue viendo adentro con su detalle.
    const pending = setupIssues(st.checks, { presetsOnly: true }).map((g) => g.label);
    const ok = await confirm({
      header: 'Iniciar operación',
      message: `Desde ahora los ciudadanos podrán enviar sus solicitudes y tu equipo empezará a recibirlas.${pending.length ? ` Queda por revisar: ${pending.join(', ')}; lo verás como aviso en cada pantalla hasta resolverlo.` : ''}`,
      acceptLabel: 'Empezar a operar',
    });
    if (!ok) return;
    goLive.mutate(undefined, {
      onSuccess: () => { notify('success', '¡Felicidades! Ya están en operación', 'Las solicitudes de los ciudadanos empiezan a llegar a tu equipo.'); nav('/'); },
      onError: notifyError,
    });
  };

  return (
    <div className="space-y-6">
      <AdminHost />
      <PageHeader
        title={setup ? 'Puesta en marcha' : 'Estado de la configuración'}
        subtitle={setup ? 'La entidad está en implementación: no recibe solicitudes por ningún canal hasta que completes estos pasos y la pases a operación.' : undefined}
        actions={<Button variant="outline" onClick={() => recheck.mutate(undefined, { onError: notifyError })} disabled={recheck.isPending}>{recheck.isPending ? <Spinner /> : <Icono nombre="mdi:refresh" />} Revisar de nuevo</Button>}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>{doneCount} de {steps.length} pasos completos</span>
          <span className="tnum text-muted-foreground">{pct}%</span>
        </div>
        <Progress value={pct} aria-label="Avance de la puesta en marcha" />
      </div>

      {!setup && <PresetIssues st={st} />}

      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <Stepper value={current} onValueChange={(n) => { if (reachable(n)) go(n); }} orientation="vertical" aria-label="Pasos de la puesta en marcha">
          <StepperNav className="w-full">
            {steps.map((s, i) => {
              const n = i + 1;
              const blocker = setup ? blockedBy(s, steps) : undefined;
              // Solo para lectores de pantalla: a la vista, el indicador ya dice si está listo, pendiente o cerrado.
              const state = s.done ? (s.warnings ? `listo, ${plural(s.warnings, 'aviso', 'avisos')}` : 'listo') : blocker ? `primero «${blocker.label}»` : `falta${s.missing === 1 ? '' : 'n'} ${s.missing}`;
              return (
                <StepperItem key={s.key} step={n} disabled={!reachable(n)} className="items-start">
                  <StepperTrigger className="w-full justify-start rounded-md p-1 text-left hover:bg-muted" aria-label={`Paso ${n}, ${s.label}, ${state}`}>
                    <StepperIndicator className={INDICATOR[s.done ? 'done' : blocker ? 'waiting' : 'missing']}>
                      {s.done ? <Icono nombre="mdi:check" className="size-3.5" aria-hidden /> : n}
                    </StepperIndicator>
                    <StepperTitle className="min-w-0 leading-snug">{s.label}</StepperTitle>
                  </StepperTrigger>
                  {n < steps.length && <StepperSeparator className="ml-[15px] group-data-[orientation=vertical]/stepper-nav:h-3" />}
                </StepperItem>
              );
            })}
          </StepperNav>
        </Stepper>

        <section aria-labelledby="paso-actual" className="min-w-0 space-y-4">
          <Step step={steps[current - 1]!} n={current} last={current === steps.length} setup={setup} ready={st.ready}
            onNext={() => go(current + 1)} onPrev={() => go(current - 1)} onGoLive={() => void pasarAOperacion()} busy={goLive.isPending} />
        </section>
      </div>
    </div>
  );
}

function Step({ step, n, last, setup, ready, onNext, onPrev, onGoLive, busy }: { step: SetupStepView; n: number; last: boolean; setup: boolean; ready: boolean; onNext: () => void; onPrev: () => void; onGoLive: () => void; busy: boolean }) {
  const Screen = SCREENS[step.key];
  // Solo se ve lo que falta: en implementación, lo obligatorio del paso; en operación todos los pasos están abiertos y
  // se ven también los recomendados.
  const locked = setup && !step.done;
  const pending = step.checks.filter((c) => !c.ok && (!setup || c.level === 'required'));
  return (
    <>
      <h2 id="paso-actual" className="text-lg font-semibold">{n}. {step.label}</h2>
      {pending.length > 0 && <SetupChecklist checks={pending} />}
      <div className="border-t border-border pt-6">
        <Screen />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={onPrev} disabled={n === 1}><Icono nombre="mdi:arrow-left" /> Anterior</Button>
        {last
          ? setup && <Button variant="success" disabled={!ready || busy} onClick={onGoLive}>{busy ? <Spinner /> : <Icono nombre="mdi:flag-outline" />} Pasar a operación</Button>
          : <Button onClick={onNext} disabled={locked}>Siguiente <Icono nombre="mdi:arrow-right" /></Button>}
      </div>
    </>
  );
}

/**
 * En operación: lo que la entidad ya traía listo (tipos y flujos, calendario, correo, almacenamiento y motor) y no
 * cumple lo obligatorio. En implementación no se muestra aquí: no detiene el paso a operación y el aviso del
 * administrador (`SetupNotice`) lo dice en las demás pantallas, con el detalle en un panel.
 */
function PresetIssues({ st }: { st: SetupStatusView }) {
  const presets = setupIssues(st.checks, { presetsOnly: true });
  const canAdvanced = useSession().can('admin.advanced');
  // La pantalla que lo resuelve puede ser de configuración avanzada (almacenamiento, motor): sin el permiso, se dice quién la ve.
  const restricted = (path: string) => !canAdvanced && !!CONFIG_SECTIONS.find((s) => path === `/configuracion/${s.to}`)?.advanced;
  if (!presets.length) return null;
  return (
    <div className="space-y-3">
      <Alert variant="destructive">
        <Icono nombre="mdi:alert-circle-outline" aria-hidden />
        <AlertTitle>Hay requisitos obligatorios sin cumplir</AlertTitle>
        <AlertDescription>Mientras sigan así, algunos casos pueden quedar sin responsable, sin correo al ciudadano o con plazos mal calculados.</AlertDescription>
      </Alert>
      {presets.map((p) => (
        <div key={p.key} className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold m-0">{p.label}</h3>
            {restricted(p.path) ? <span className="text-xs text-muted-foreground">Lo resuelve quien administra la configuración avanzada</span> : <Button asChild size="sm" variant="outline"><Link to={p.path}>Abrir en Configuración</Link></Button>}
          </div>
          <SetupChecklist checks={p.checks} />
        </div>
      ))}
    </div>
  );
}
